import { listPaddocksByOrganizationQueryOptions } from "@packages/api-client/hooks/paddockController";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MapPin, MapPinOff } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	type LegendItem,
	MapLegend,
	type PaddockForMap,
	PaddockMap,
} from "@/components/grass";
import { basemapOptions } from "@/components/grass/basemap";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DetailSkeleton } from "@/components/ui/skeleton";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { cn } from "@/utils/utils";
import { PaddockStatusPill } from "../../../-components/grassPresentation";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/grass-production/farms/$farmId/map/",
)({
	// Map tiles need the network; offline tile caching is a separate, later
	// investment (see the offline module roadmap).
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	component: PaddockMapPage,
});

/**
 * Whether `PaddockMap` will render a basemap switcher here — a pure question
 * about the catalogue, never about the user or their stored preference, so it
 * needs no hook and duplicates no preference state.
 *
 * It is the legend's cue to step down a row and make space for that control.
 * Tied to the control actually rendering rather than to a release: with one
 * basemap registered there is no switcher, and the legend keeps its position.
 * `lg` and not `sm` because the legend is top-right until `lg` (MapLegend.tsx).
 */
const legendOffsetClass =
	basemapOptions("view").length > 1 ? "top-16 lg:top-auto" : undefined;

function PaddockMapPage() {
	const { t } = useTranslation();
	const { orgId, farmId } = Route.useParams();
	const [selectedPaddock, setSelectedPaddock] = useState<PaddockForMap | null>(
		null,
	);
	const colorBy = "status";

	const { data: paddocksData, isLoading } = useQuery(
		listPaddocksByOrganizationQueryOptions(orgId, { establishmentId: farmId }),
	);

	const paddocks = (paddocksData?.data ?? []) as unknown as PaddockForMap[];

	const paddocksWithBoundary = useMemo(
		() => paddocks.filter((p) => p.boundaryGeoJson),
		[paddocks],
	);

	const legendItems = useMemo<LegendItem[]>(() => {
		if (colorBy === "status") {
			const statusCounts: Record<string, number> = {};
			paddocks.forEach((p) => {
				statusCounts[p.status] = (statusCounts[p.status] || 0) + 1;
			});
			return [
				{
					color: "#22c55e",
					label: t("grass.paddock.statuses.ESTABLISHED"),
					count: statusCounts.ESTABLISHED || 0,
				},
				{
					color: "#f59e0b",
					label: t("grass.paddock.statuses.RENOVATION"),
					count: statusCounts.RENOVATION || 0,
				},
				{
					color: "#ef4444",
					label: t("grass.paddock.statuses.DEGRADED"),
					count: statusCounts.DEGRADED || 0,
				},
				{
					color: "#3b82f6",
					label: t("grass.paddock.statuses.REST"),
					count: statusCounts.REST || 0,
				},
			];
		}
		return [
			{ color: "#ef4444", label: t("grass.map.biomass.low") },
			{ color: "#f59e0b", label: t("grass.map.biomass.medium") },
			{ color: "#22c55e", label: t("grass.map.biomass.good") },
			{ color: "#15803d", label: t("grass.map.biomass.high") },
		];
	}, [paddocks, t]);

	if (isLoading) {
		return <DetailSkeleton />;
	}

	return (
		<div className="flex flex-col gap-6">
			<PageHeader title={t("grass.map.title")} showBackButton />

			{paddocks.length > 0 && paddocksWithBoundary.length === 0 && (
				<div className="text-sm text-warning flex items-center gap-2">
					<MapPinOff className="h-4 w-4" />
					{t("grass.map.noBoundaries")}
				</div>
			)}

			<div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
				<div className="relative h-[60vh] min-h-80 lg:col-span-3 lg:h-[600px]">
					<PaddockMap
						paddocks={paddocks}
						selectedPaddockId={selectedPaddock?.id}
						onPaddockClick={setSelectedPaddock}
						colorBy={colorBy}
						height="100%"
					/>
					<MapLegend items={legendItems} className={legendOffsetClass} />
				</div>

				<div className="lg:col-span-1 flex flex-col gap-4">
					{/* Selected Paddock Details */}
					{selectedPaddock && (
						<Card>
							<CardHeader className="pb-2">
								<CardTitle className="text-lg">
									{selectedPaddock.name}
								</CardTitle>
							</CardHeader>
							<CardContent className="space-y-3">
								<div className="flex justify-between text-sm">
									<span className="text-base-content/70">
										{t("grass.paddock.fields.area")}
									</span>
									<span>{selectedPaddock.areaHa} ha</span>
								</div>
								<div className="flex justify-between text-sm">
									<span className="text-base-content/70">
										{t("grass.paddock.fields.status")}
									</span>
									<PaddockStatusPill
										status={selectedPaddock.status}
										size="sm"
									/>
								</div>
								{selectedPaddock.latestMeasurement && (
									<div className="flex justify-between text-sm">
										<span className="text-base-content/70">
											{t("grass.measurement.fields.biomass")}
										</span>
										<span>
											{selectedPaddock.latestMeasurement.biomassKgHa} kg/ha
										</span>
									</div>
								)}
								{!selectedPaddock.boundaryGeoJson && (
									<div className="text-xs text-warning flex items-center gap-1">
										<MapPinOff className="h-3 w-3" />
										{t("grass.map.noBoundary")}
									</div>
								)}
								<Link
									to="/app/org/$orgId/grass-production/farms/$farmId/paddocks/$paddockId"
									params={{ orgId, farmId, paddockId: selectedPaddock.id }}
									className="btn btn-primary btn-sm w-full mt-4"
								>
									{t("grass.map.viewDetails")}
								</Link>
							</CardContent>
						</Card>
					)}

					{/* Paddock List */}
					<Card className="flex-1">
						<CardHeader className="pb-2">
							<CardTitle className="text-base flex items-center justify-between">
								{t("grass.map.paddockList")}
								<Badge variant="secondary">{paddocks.length}</Badge>
							</CardTitle>
						</CardHeader>
						<CardContent className="p-0">
							{paddocks.length === 0 ? (
								<div className="p-4 text-center text-base-content/70 text-sm">
									{t("grass.paddock.list.empty.title")}
								</div>
							) : (
								<ScrollArea className="h-100">
									<div className="divide-y divide-base-300">
										{paddocks.map((paddock) => (
											<button
												key={paddock.id}
												type="button"
												onClick={() => setSelectedPaddock(paddock)}
												className={cn(
													"w-full p-3 text-left hover:bg-base-200 transition-colors",
													selectedPaddock?.id === paddock.id && "bg-base-200",
												)}
											>
												<div className="flex items-center justify-between">
													<div className="flex items-center gap-2">
														{paddock.boundaryGeoJson ? (
															<MapPin className="h-4 w-4 text-success" />
														) : (
															<MapPinOff className="h-4 w-4 text-base-content/40" />
														)}
														<span className="font-medium text-sm">
															{paddock.name}
														</span>
													</div>
													<PaddockStatusPill
														status={paddock.status}
														size="sm"
													/>
												</div>
												<div className="mt-1 text-xs text-base-content/70 ml-6">
													{paddock.areaHa} ha
													{paddock.code && ` • ${paddock.code}`}
												</div>
											</button>
										))}
									</div>
								</ScrollArea>
							)}
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
}
